/**
 * Colour keyframe maths - what colour a lights lane is at any point in time.
 *
 * ONE implementation for every surface that draws or plays a colour lane: the
 * timeline editor v2 draws its fades from it, and the Legacy timeline's lane
 * swatch samples it. Lifted out of `TimelineEditor.tsx` 2026-10-01, verbatim
 * (DEC-049's spaces and curves), when v2 started drawing colour changes as
 * keyframes with a transition between them rather than as blocks. Whatever
 * plays a colour lane for real (OPBridge, when it has a colour runtime) has to
 * agree with this file, so it stays free of UI.
 *
 * A lane's colour keyframes are `ColorMarkerStep`s. The segment from one
 * keyframe to the next is shaped by the OUTGOING keyframe: `curve` ('hold' is
 * a cut on the next keyframe's beat, anything else a fade), `space` (which
 * colour space the fade blends in) and `path` (which way round the hue wheel).
 */

import type { ColorMarkerStep, TimelineLane } from '@opbuttons/schema';
import { isColorMarkerStep, unrollPatterns } from '@opbuttons/schema';

export type Rgb01 = { r: number; g: number; b: number; w?: number; wTemp?: number };

/** Below this saturation (HSL) or chroma (OKLCH) a colour is a grey and its
 *  hue means nothing. */
const NEAR_GREY = 1e-4;

/** Apply an easing curve to a normalized 0..1 progress `t`. Same names as
 *  ValuePointCurve on the schema; keeps the sample math + the picker menu
 *  in sync. */
export function applyCurve(t: number, curve: 'linear' | 'hold' | 'ease-in' | 'ease-out' | 'ease-in-out' | undefined): number {
  if (t <= 0) return 0;
  if (t >= 1) return 1;
  switch (curve) {
    case 'hold': return 0;
    case 'ease-in':      return t * t;
    case 'ease-out':     return 1 - (1 - t) * (1 - t);
    case 'ease-in-out':  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
    default:             return t;
  }
}

// ─── Color-space conversions used by the color-lane sampler ───────
//
// Everything below operates on 0..1 R/G/B (matches ColorMarkerStep.color).
// HSL uses hue in 0..1 (i.e. 360deg / 360) so lerps stay unit-scale.
// OKLCH conversions run through linear-light RGB per the Björn Ottosson
// derivation; L is 0..1 and C is ~0..0.4 in gamut for our stage colours.

function rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return { h: 0, s: 0, l };
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return { h, s, l };
}

function hslToRgb(h: number, s: number, l: number): { r: number; g: number; b: number } {
  if (s === 0) return { r: l, g: l, b: l };
  const hue2rgb = (p: number, q: number, t: number): number => {
    let tt = t;
    if (tt < 0) tt += 1;
    if (tt > 1) tt -= 1;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return {
    r: hue2rgb(p, q, h + 1 / 3),
    g: hue2rgb(p, q, h),
    b: hue2rgb(p, q, h - 1 / 3),
  };
}

/** sRGB companding (0..1 → linear 0..1). Standard IEC 61966-2-1. */
function srgbToLinear(c: number): number {
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}
function linearToSrgb(c: number): number {
  return c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
}

/** OKLab per https://bottosson.github.io/posts/oklab/. */
function rgbToOklab(r: number, g: number, b: number): { L: number; a: number; b: number } {
  const lr = srgbToLinear(r);
  const lg = srgbToLinear(g);
  const lb = srgbToLinear(b);
  const l = 0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb;
  const m = 0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb;
  const s = 0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb;
  const l_ = Math.cbrt(l);
  const m_ = Math.cbrt(m);
  const s_ = Math.cbrt(s);
  return {
    L: 0.2104542553 * l_ + 0.7936177850 * m_ - 0.0040720468 * s_,
    a: 1.9779984951 * l_ - 2.4285922050 * m_ + 0.4505937099 * s_,
    b: 0.0259040371 * l_ + 0.7827717662 * m_ - 0.8086757660 * s_,
  };
}
function oklabToRgb(L: number, a: number, b: number): { r: number; g: number; b: number } {
  const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = L - 0.0894841775 * a - 1.2914855480 * b;
  const l = l_ * l_ * l_;
  const m = m_ * m_ * m_;
  const s = s_ * s_ * s_;
  const lr = 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s;
  const lg = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s;
  const lb = -0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s;
  return {
    r: Math.max(0, Math.min(1, linearToSrgb(lr))),
    g: Math.max(0, Math.min(1, linearToSrgb(lg))),
    b: Math.max(0, Math.min(1, linearToSrgb(lb))),
  };
}
/** OKLCH is OKLab in polar form. Hue in 0..1 for lerp-friendliness. */
function rgbToOklch(r: number, g: number, b: number): { L: number; C: number; H: number } {
  const { L, a, b: bb } = rgbToOklab(r, g, b);
  const C = Math.hypot(a, bb);
  const H = ((Math.atan2(bb, a) / (2 * Math.PI)) + 1) % 1;
  return { L, C, H };
}
function oklchToRgb(L: number, C: number, H: number): { r: number; g: number; b: number } {
  const rad = H * 2 * Math.PI;
  return oklabToRgb(L, Math.cos(rad) * C, Math.sin(rad) * C);
}

/** Lerp two hues in 0..1 hue-wheel space with the requested direction.
 *  short = smaller arc; long = larger arc. When source and destination
 *  are equal, returns either - no wheel motion required. */
export function lerpHue(h0: number, h1: number, t: number, path: 'short' | 'long'): number {
  const diff = h1 - h0;
  // Canonical short delta in (-0.5, 0.5]. If input is already short, delta === diff.
  let delta = diff - Math.round(diff);
  // Flip to long path if requested (and there IS motion).
  if (path === 'long' && delta !== 0) {
    delta = delta > 0 ? delta - 1 : delta + 1;
  }
  const h = h0 + delta * t;
  return ((h % 1) + 1) % 1;
}

/** The colour `t` of the way (0..1, before the curve) along the segment from
 *  keyframe `a` to keyframe `b`, shaped by `a`: its `curve` (a 'hold' stays
 *  on `a` until `b`), its `space` (defaults sRGB) and its `path` (defaults
 *  short, ignored on sRGB). White (`w`) blends linearly when either has it. */
export function sampleColorSegment(a: ColorMarkerStep, b: ColorMarkerStep, rawT: number): Rgb01 {
  const t = applyCurve(rawT, a.curve);
  const space = a.space ?? 'sRGB';
  const path = a.path ?? 'short';
  const lerp = (p: number, q: number): number => p + (q - p) * t;

  let rgb: { r: number; g: number; b: number };
  if (space === 'HSL') {
    const A = rgbToHsl(a.color.r, a.color.g, a.color.b);
    const B = rgbToHsl(b.color.r, b.color.g, b.color.b);
    // A grey (White, Off) has no hue; `rgbToHsl` calls it 0, which is red, so
    // Blue to White swept through pink and red. Borrow the other end's hue,
    // the OKLCH branch's rule (2026-10-01, seen in a render, not reasoned).
    let h: number;
    if (A.s < NEAR_GREY && B.s < NEAR_GREY) h = A.h;
    else if (A.s < NEAR_GREY) h = B.h;
    else if (B.s < NEAR_GREY) h = A.h;
    else h = lerpHue(A.h, B.h, t, path);
    const s = lerp(A.s, B.s);
    const l = lerp(A.l, B.l);
    rgb = hslToRgb(h, s, l);
  } else if (space === 'OKLCH') {
    const A = rgbToOklch(a.color.r, a.color.g, a.color.b);
    const B = rgbToOklch(b.color.r, b.color.g, b.color.b);
    // If either endpoint is (near-)grey, hue is undefined; fall back
    // to the other endpoint's hue so we don't sweep through arbitrary
    // colours on a black<->white fade.
    let H: number;
    if (A.C < NEAR_GREY && B.C < NEAR_GREY) H = A.H;
    else if (A.C < NEAR_GREY) H = B.H;
    else if (B.C < NEAR_GREY) H = A.H;
    else H = lerpHue(A.H, B.H, t, path);
    rgb = oklchToRgb(lerp(A.L, B.L), lerp(A.C, B.C), H);
  } else {
    // sRGB (default) - componentwise linear on stored RGB.
    rgb = {
      r: lerp(a.color.r, b.color.r),
      g: lerp(a.color.g, b.color.g),
      b: lerp(a.color.b, b.color.b),
    };
  }

  const out: Rgb01 = rgb;
  const aw = a.color.w ?? 0;
  const bw = b.color.w ?? 0;
  if (a.color.w !== undefined || b.color.w !== undefined) {
    out.w = lerp(aw, bw);
  }
  // The whites' warm-to-cool (OPBridge S329) fades like `w`; one side without it is both whites alike (0.5).
  if (a.color.wTemp !== undefined || b.color.wTemp !== undefined) {
    out.wTemp = lerp(a.color.wTemp ?? 0.5, b.color.wTemp ?? 0.5);
  }
  return out;
}

/** Evaluate a `dmx-rig`/`dmx-fixture` lane's colour markers at `positionBars`. Interpolates R/G/B/W
 *  between consecutive markers with the outgoing marker's curve; holds
 *  flat before the first / after the last. Interpolation space is the
 *  outgoing marker's `space` (defaults sRGB); hue direction is its
 *  `path` (defaults short, ignored on sRGB). Returns null when the lane
 *  doesn't exist or has no markers. */
export function sampleColorLane(
  lane: TimelineLane,
  positionBars: number,
): Rgb01 | null {
  // Patterns written out (2026-10-20): a pattern block plays as its keys.
  const pts = unrollPatterns(lane.steps)
    .filter(isColorMarkerStep)
    .slice()
    .sort((a, b) => a.position - b.position);
  if (pts.length === 0) return null;
  if (positionBars <= pts[0]!.position) return { ...pts[0]!.color };
  const last = pts[pts.length - 1]!;
  if (positionBars >= last.position) return { ...last.color };
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i]!;
    const b = pts[i + 1]!;
    if (positionBars >= a.position && positionBars <= b.position) {
      const span = b.position - a.position;
      if (span <= 0) return { ...b.color };
      return sampleColorSegment(a, b, (positionBars - a.position) / span);
    }
  }
  return { ...last.color };
}

/** `n` evenly spaced colours along a segment, first at `a`, last just before
 *  `b` takes over - what a drawn fade is made of. A cut is `a` throughout. */
export function colorSegmentStops(a: ColorMarkerStep, b: ColorMarkerStep, n: number): Rgb01[] {
  const count = Math.max(2, Math.round(n));
  const out: Rgb01[] = [];
  for (let i = 0; i < count; i++) out.push(sampleColorSegment(a, b, Math.min(1 - 1e-6, i / (count - 1))));
  return out;
}
